import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { waLink } from "@/lib/whatsapp";
import { setSeo, resetSeo } from "@/lib/seo";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import * as Icons from "lucide-react";
import { ArrowRight, MessageCircle, Clock, User, Star, Loader2, Sparkles } from "lucide-react";

function toPascal(s) { return (s || "sparkles").split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(""); }

export default function WellnessGoal() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { settings } = useApp();
  const [data, setData] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setData(null);
    api.get(`/wellness-goals/${slug}`).then((r) => setData(r.data)).catch(() => nav("/shop"));
  }, [slug, nav]);

  useEffect(() => {
    if (data) setSeo({ title: `${data.goal.name} — Wellness Goal`, description: `Shop organic products, read expert articles and connect with wellness consultants for ${data.goal.name} at LIVENEAT Organic.` });
    return () => resetSeo();
  }, [data]);

  if (!data) return <div className="flex justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-forest" /></div>;
  const { goal, products, consultants, articles } = data;
  const Icon = Icons[toPascal(goal.icon)] || Sparkles;

  return (
    <div>
      <section className="relative overflow-hidden leaf-pattern" style={{ backgroundColor: goal.color + "22" }}>
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-24 text-center">
          <span className="w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: goal.color + "33" }}>
            <Icon className="w-8 h-8" style={{ color: goal.color }} />
          </span>
          <span className="text-sm font-500 uppercase tracking-wider" style={{ color: goal.color }}>Shop by Wellness Goal</span>
          <h1 className="font-heading text-4xl lg:text-6xl text-forest tracking-tight mt-2">{goal.name}</h1>
          <p className="text-brown/70 mt-4 max-w-xl mx-auto">Everything you need for {goal.name.toLowerCase()} — carefully curated products, expert articles and personalised consultant guidance.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-16">
        <div className="flex items-end justify-between mb-8 gap-4">
          <h2 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight">Recommended Products</h2>
          <Link to={`/shop?wellness_goal=${goal.id}`} className="hidden sm:inline-flex items-center gap-1.5 text-forest font-500 hover:text-terracotta transition-colors">View all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        {products.length === 0 ? <p className="text-muted-foreground">No products yet for this goal.</p> : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {articles.length > 0 && (
        <section className="bg-sand">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-16">
            <h2 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight mb-8">Read & Learn</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {articles.map((b) => (
                <Link key={b.id} to={`/blog/${b.slug}`} className="group bg-white rounded-[24px] overflow-hidden soft-shadow hover:soft-shadow-lg transition-shadow">
                  <div className="aspect-[16/10] overflow-hidden"><img src={b.image} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                  <div className="p-5">
                    <span className="text-xs bg-mustard/25 text-brown px-3 py-1 rounded-full font-500">{b.category}</span>
                    <h3 className="font-heading text-xl text-forest mt-2 group-hover:text-terracotta transition-colors leading-tight">{b.title}</h3>
                    <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground"><span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" />{b.author}</span><span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{b.read} min</span></div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {consultants.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-16">
          <h2 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight mb-8">Talk to an Expert</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {consultants.map((c) => (
              <div key={c.id} className="bg-white rounded-[24px] soft-shadow overflow-hidden flex flex-col">
                <div className="aspect-[4/3] overflow-hidden bg-sand"><img src={c.image} alt={c.name} className="w-full h-full object-cover" /></div>
                <div className="p-5 flex flex-col flex-1">
                  <span className="text-xs bg-sage/20 text-forest px-3 py-1 rounded-full font-500 w-fit">{c.specialization}</span>
                  <h3 className="font-heading text-2xl text-forest mt-2">{c.name}</h3>
                  <p className="text-sm text-brown/70 flex-1 mt-1 line-clamp-2">{c.bio}</p>
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    <Link to="/consultants"><Button className="w-full rounded-full bg-forest hover:bg-earth text-cream">Connect</Button></Link>
                    <a href={waLink(settings.whatsapp_number, `Hi LIVENEAT Organic, I'd like to connect with ${c.name} about ${goal.name}.`)} target="_blank" rel="noreferrer"
                      className="inline-flex items-center justify-center gap-1.5 bg-[#25D366] text-white rounded-full h-10 text-sm font-500"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
